import { useState } from 'react';
import { Search, FileText, Eye } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Pagination } from '../components/ui/pagination';
import { LoadingTable } from '../components/ui/loading-states';
import { ErrorState } from '../components/ui/error-state';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  useMarkirovkaDocuments,
  useMarkirovkaDocumentMarks,
  useToggleDocumentExcluded,
} from '../hooks/useMarkirovkaDocuments';
import type {
  MarkirovkaDocumentFilters,
  MarkirovkaIncomingDocument,
} from '../types/markirovka-stock.types';

const PAGE_SIZE = 50;

export function MarkirovkaDocuments() {
  const [filters, setFilters] = useState<MarkirovkaDocumentFilters>({
    page: 1,
    limit: PAGE_SIZE,
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectedDoc, setSelectedDoc] =
    useState<MarkirovkaIncomingDocument | null>(null);

  const { data, isLoading, error, refetch } = useMarkirovkaDocuments(filters);
  const toggleMutation = useToggleDocumentExcluded();

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (filters.limit ?? PAGE_SIZE)));

  const applySearch = () =>
    setFilters((f) => ({ ...f, search: searchInput || undefined, page: 1 }));

  const setExcludedFilter = (value: 'all' | 'true' | 'false') =>
    setFilters((f) => ({
      ...f,
      is_excluded: value === 'all' ? undefined : value === 'true',
      page: 1,
    }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Накладные</h1>
        <p className="text-muted-foreground">
          Входящие накладные edo.markirovka.kz. Бесплатные госпоставки можно
          пометить «вне учёта» — они исключаются из сверки и выгрузки в 1С.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Список накладных</CardTitle>
          <CardDescription>
            Поиск по номеру накладной (paper_num) или edo_id.
          </CardDescription>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по накладной / edo_id"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                className="pl-9"
              />
            </div>
            <Button variant="outline" onClick={applySearch}>
              Найти
            </Button>
            <div className="flex items-center gap-1">
              {(['all', 'false', 'true'] as const).map((v) => (
                <Button
                  key={v}
                  variant={
                    (filters.is_excluded === undefined && v === 'all') ||
                    (filters.is_excluded === true && v === 'true') ||
                    (filters.is_excluded === false && v === 'false')
                      ? 'default'
                      : 'outline'
                  }
                  size="sm"
                  onClick={() => setExcludedFilter(v)}
                >
                  {v === 'all'
                    ? 'Все'
                    : v === 'false'
                      ? 'В учёте'
                      : 'Вне учёта'}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={8} columns={6} />
          ) : error ? (
            <ErrorState
              title="Не удалось загрузить накладные"
              description={(error as Error)?.message}
              onAction={() => refetch()}
            />
          ) : rows.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              Накладные не найдены
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Накладная</TableHead>
                    <TableHead>Дата</TableHead>
                    <TableHead>Отправитель</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-center">Вне учёта</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">
                        {doc.paper_num || `№${doc.external_id}`}
                        {doc.edo_id && (
                          <div className="text-xs text-muted-foreground">
                            edo: {doc.edo_id}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {doc.doc_date
                          ? new Date(doc.doc_date).toLocaleDateString('ru-RU')
                          : '—'}
                      </TableCell>
                      <TableCell className="max-w-[260px] truncate">
                        {doc.sender_name || doc.sender_bin || '—'}
                      </TableCell>
                      <TableCell>
                        {doc.status_name ? (
                          <Badge variant="secondary">{doc.status_name}</Badge>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={doc.is_excluded}
                          disabled={toggleMutation.isPending}
                          onCheckedChange={(checked) =>
                            toggleMutation.mutate({
                              id: doc.id,
                              isExcluded: checked,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedDoc(doc)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4">
                <Pagination
                  currentPage={filters.page ?? 1}
                  totalPages={totalPages}
                  onPageChange={(p) => setFilters((f) => ({ ...f, page: p }))}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <DocumentMarksDialog
        doc={selectedDoc}
        onClose={() => setSelectedDoc(null)}
      />
    </div>
  );
}

interface DocumentMarksDialogProps {
  doc: MarkirovkaIncomingDocument | null;
  onClose: () => void;
}

function DocumentMarksDialog({ doc, onClose }: DocumentMarksDialogProps) {
  const { data, isLoading, error, refetch } = useMarkirovkaDocumentMarks(
    doc?.id ?? null,
  );

  return (
    <Dialog open={!!doc} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {doc?.paper_num || `Накладная №${doc?.external_id}`}
          </DialogTitle>
          <DialogDescription>
            {doc?.sender_name} ·{' '}
            {doc?.doc_date
              ? new Date(doc.doc_date).toLocaleDateString('ru-RU')
              : ''}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <LoadingTable rows={5} columns={4} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить состав"
            description={(error as Error)?.message}
            onAction={() => refetch()}
          />
        ) : (data?.data?.length ?? 0) === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            По этой накладной нет КМ на текущих остатках
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Наименование</TableHead>
                <TableHead>GTIN</TableHead>
                <TableHead>Партия</TableHead>
                <TableHead>Годен до</TableHead>
                <TableHead>CIS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data.map((m) => (
                <TableRow key={m.cis}>
                  <TableCell className="max-w-[320px] truncate">
                    {m.product_name || '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{m.gtin}</TableCell>
                  <TableCell>{m.party_num || '—'}</TableCell>
                  <TableCell>{m.limit_date || '—'}</TableCell>
                  <TableCell className="font-mono text-xs break-all max-w-[260px]">
                    {m.cis}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
